import React from 'react';

import type { IconName } from '../../components/Icon/Icon';

/** What a page sets on the area header in place of its own (Game Lists drilled down on a phone): the level it is at
 *  names the header, and the back button goes up a level rather than to the hub. */
export type HeaderOverride = {
  /** Shown as given (a page's or a list's name); without it, the area's name. */
  title?: string;
  /** Where the back button goes, and its name; without them, to the hub. */
  onBack?: () => void;
  backLabel?: string;
  /** The title as a control (renaming what it names), with its glyph and accessible name. */
  onTitleClick?: () => void;
  titleIcon?: IconName;
  titleLabel?: string;
  /** A tag under the title (the brand's own list it names: "User list"). */
  tag?: string;
  /** The page's action is a single icon: on a phone it shares the title's row instead of a row of its own. */
  inlineAction?: boolean;
  /** The action has opened across the row (see BackHeaderProps.actionOpen). */
  actionOpen?: boolean;
};

/** The area header's slot for a page's action, as the Website frame hosts it (see `useHeaderActionHost`). */
type HeaderActionHost = {
  /** The element the action is drawn into: null until the header has drawn the slot. */
  slot: HTMLElement | null;
  /** A page asks for the slot (true) or lets it go (false). */
  want: (on: boolean) => void;
  /** A page sets the header's override (see `useWebsiteHeaderOverride`), or clears it (null). */
  setOverride: (override: HeaderOverride | null) => void;
  /** The frame draws the area header, so an override shows. */
  hasHeader: boolean;
};

const HeaderAction = React.createContext<HeaderActionHost | null>(null);

export const HeaderActionProvider = HeaderAction.Provider;

/**
 * For the Website frame (WebsiteShell, WebsitePage): the slot at the right of the area header's row (BackHeader's
 * action), beside the area's name, which a page fills with `useWebsiteHeaderAction`. The header draws it only while a
 * page asks for it (`wanted`), so no other area's title gives up room to an empty slot.
 */
export const useHeaderActionHost = (
  hasHeader = false,
): {
  value: HeaderActionHost;
  wanted: boolean;
  setSlot: (el: HTMLElement | null) => void;
  override: HeaderOverride | null;
} => {
  const [wanted, setWanted] = React.useState(0);
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  const [override, setOverride] = React.useState<HeaderOverride | null>(null);
  const want = React.useCallback((on: boolean) => setWanted((n) => n + (on ? 1 : -1)), []);
  const value = React.useMemo(() => ({ slot, want, setOverride, hasHeader }), [slot, want, hasHeader]);
  return { value, wanted: wanted > 0, setSlot, override };
};

/**
 * For a page in the Website frame: asks for the slot at the right of the area header's row for as long as it is
 * mounted (and `enabled`, while it has an action to show), and returns the element to portal its action into. Null outside the frame (and until the header has drawn
 * the slot), where the page keeps its action in its own content.
 */
export const useWebsiteHeaderAction = (enabled = true): HTMLElement | null => {
  const host = React.useContext(HeaderAction);
  const want = enabled ? host?.want : undefined;
  React.useEffect(() => {
    if (!want) return undefined;
    want(true);
    return () => want(false);
  }, [want]);
  return enabled ? host?.slot ?? null : null;
};

/**
 * For a page in the Website frame: sets the area header's title, back and action layout for as long as `override` is
 * given (null leaves the header its own). Its callbacks are read when pressed, so a page may hand new ones each render.
 * Says whether it shows: false outside the frame, or where the frame draws no area header (the page keeps its own way
 * back then). Set (and cleared) in a layout effect, so the header changes in the same frame as the page, which drops its
 * own title and way back on the render that returns true: a passive effect painted a frame of the area's own header
 * first (its name, the action on a row of its own), and what is pinned under the header moved twice.
 */
export const useWebsiteHeaderOverride = (override: HeaderOverride | null): boolean => {
  const host = React.useContext(HeaderAction);
  const latest = React.useRef(override);
  latest.current = override;
  const setOverride = host?.setOverride;
  const key = override
    ? [
        override.title ?? '',
        override.backLabel ?? '',
        override.onBack ? 'back' : '',
        override.onTitleClick ? 'title-press' : '',
        override.titleIcon ?? '',
        override.titleLabel ?? '',
        override.tag ?? '',
        override.inlineAction ? 'inline' : '',
        override.actionOpen ? 'open' : '',
      ].join('\u0000')
    : '';
  React.useLayoutEffect(() => {
    const given = latest.current;
    if (!setOverride || !given) return undefined;
    setOverride({
      ...given,
      onBack: given.onBack ? () => latest.current?.onBack?.() : undefined,
      onTitleClick: given.onTitleClick ? () => latest.current?.onTitleClick?.() : undefined,
    });
    return () => setOverride(null);
  }, [setOverride, key]);
  return Boolean(host?.hasHeader && override);
};
